import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {
    addCommas,
    addFromColumn,
    sortCurrency,
    sortNumber,
    toCurrency,
} from '../shared/functions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import {convertStartAndEndDatesForFile} from '../shared/printFunctions';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import FlatButton from "material-ui/FlatButton";
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import VIPCustomersTableIcon from 'material-ui/svg-icons/action/card-giftcard';
import DetailIcon from 'material-ui/svg-icons/image/remove-red-eye';

/**
 * Class for displaying VIP Customers Summary in a table
 *
 */
class VIPTrendsVipCustomersTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'VIP Customer ID', key: 'customer_id'},
                {label: 'First Name', key: 'first_name'},
                {label: 'Last Name', key: 'last_name'},
                {label: 'Email', key: 'email'},
                {label: 'Phone Number', key: 'telephone'},
                {label: 'City', key: 'city'},
                {label: 'State', key: 'state'},
                {label: 'Zipcode', key: 'zip'},
                {label: 'Active', key: 'active'},
                {label: '$ Spent that Earned Points', key: 'amount_spent_that_earned_points'},
                {label: 'Points Spent', key: 'pts_spent'},
                {label: 'Points Spent $ Value', key: 'pts_spent_value'},
                {label: 'Number of Sales', key: 'num_sales'}
            ],
        };
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
    }

    componentWillMount() {
        this.processData(this.props.listData);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.listData !== this.props.listData) {
            this.setState({
                summaryList: nextProps.listData,
                summaryTableData: nextProps.listData
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.listData !== this.props.listData) {
            this.processData(this.props.listData);
        }
    }

    /* Data Processing ------------------------------ */

    /**
     * Set data for use by table
     *
     * @param listData
     */
    processData(listData) {
        this.setState({
            summaryList: listData,
            summaryTableData: listData
        });
    }

    /* Table Building ------------------------------ */

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortChange(event, table) {
        const tableData = getTableState(table);
        this.setState({
            summaryTableData: tableData
        });
    }

    /**
     * Update selected table data based on filter and lift state
     *
     * @param event
     * @param table
     */
    handleFilterChange(event, table) {
        const tableData = getTableState(table);
        this.setState({
            summaryTableData: tableData
        });
    }

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            allowLoyaltySelection,
            endDate,
            loyaltyIconFunction,
            startDate,
            system,
            tableKey,
            tableLabel
        } = this.props;

        const {
            summaryList,
            summaryTableData,
            summaryTableHeaders
        } = this.state;

        const dateString = convertStartAndEndDatesForFile(startDate, endDate);
        const defaultSort = setDefaultTableSort('customer_id');
        const iconStyle = (allowLoyaltySelection) ? tableStyles.tableIconButton : tableStyles.disabledTableIconButton;

        // Headers and columns for ReactTable
        const tableColumns = [
            {
                Header: 'Customer',
                columns: [
                    {
                        Header: () => (<span>VIP<br/>Customer ID</span>),
                        accessor: 'customer_id',
                        Cell: row => (
                            <span>
                                <span>{row.row.customer_id}</span>
                                <DetailIcon
                                    onClick={(e) => loyaltyIconFunction(e, row.row)}
                                    style={iconStyle}
                                />
                            </span>
                        ),
                        width: 170,
                        style: tableStyles.right,
                    },
                    {
                        Header: () => (<span>First<br/>Name</span>),
                        accessor: 'first_name',
                    },
                    {
                        Header: () => (<span>Last<br/>Name</span>),
                        accessor: 'last_name',
                    },
                    {
                        Header: 'Email',
                        accessor: 'email',
                        width: 200
                    },
                    {
                        Header: () => (<span>Phone<br/>Number</span>),
                        accessor: 'telephone',
                    },
                    {
                        Header: 'City',
                        accessor: 'city',
                    },
                    {
                        Header: 'State',
                        accessor: 'state',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'Zipcode',
                        accessor: 'zip',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'Active',
                        accessor: 'active',
                        filterable: false,
                        headerStyle: tableStyles.thLast,
                    }
                ]
            },
            {
                Header: 'Transactions',
                columns: [
                    {
                        Header: () => (<span>$ Spent<br/>that<br/>Earned<br/>Points</span>),
                        accessor: 'amount_spent_that_earned_points',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(summaryTableData, 'amount_spent_that_earned_points'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>Points<br/>Spent</span>),
                        accessor: 'pts_spent',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(summaryTableData, 'pts_spent'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>Points Spent<br/>$ Value</span>),
                        accessor: 'pts_spent_value',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(summaryTableData, 'pts_spent_value'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>Number<br/>of sales</span>),
                        accessor: 'num_sales',
                        sortMethod: (a, b) => sortNumber(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addCommas(addFromColumn(summaryTableData, 'num_sales'))}
                            </span>
                        )
                    }
                ]
            }
        ];

        return (
            <div>
                {summaryList.length > 0 &&
                <div className="reports-table-container">

                    {/* Table Header: VIP Customers */}
                    <div className="reports-table-title table-table-header">
                        <div className="btn-report-container report-header-button-container">
                            {/* Export VIP Trends - VIP Customers */}
                            <FlatButton
                                primary
                                type="button"
                                label=""
                                style={commonStyles.headerButton}
                            >
                                <ExportIcon style={commonStyles.headerButtonIcon}/>
                                <CSVLink
                                    data={summaryTableData}
                                    headers={summaryTableHeaders}
                                    filename={'VIP-Trends-Customers-' + system + '-' + dateString + '.csv'}
                                    onClick={(e) => this.handleExportClick(e)}
                                    target="_blank"
                                    style={commonStyles.headerButtonLink}
                                >
                                    Export VIP Customers
                                </CSVLink>
                            </FlatButton>
                        </div>
                        <h3 className="table-table-header-title">
                            <VIPCustomersTableIcon style={commonStyles.summaryIcon}/>
                            <span>{tableLabel}</span>
                        </h3>
                    </div>

                    {/* Table: VIP Customers */}
                    <ReactTable
                        key={`table-vip-customers-${tableKey}`}
                        data={summaryList}
                        columns={tableColumns}
                        defaultPageSize={5}
                        showPagination={true}
                        defaultSorted={defaultSort}
                        filterable
                        defaultFilterMethod={filterCaseInsensitive}
                        onSortedChange={(e) => this.handleSortChange(e, this.customersReactTable)}
                        onFilteredChange={(e) => this.handleFilterChange(e, this.customersReactTable)}
                        ref={(r) => this.customersReactTable = r}
                        resizable={false}
                        minRows={1}
                        className="-striped overflow no-print"
                    />
                    <ReactTable
                        key={`table-vip-customers-print-${tableKey}`}
                        data={summaryTableData}
                        columns={tableColumns}
                        defaultPageSize={10000}
                        showPagination={false}
                        defaultSorted={defaultSort}
                        filterable
                        resizable={false}
                        minRows={1}
                        className="print-only"
                    />
                </div>
                }
            </div>
        )
    }
}

VIPTrendsVipCustomersTable.propTypes = {
    addField: PropTypes.bool,
    allowLoyaltySelection: PropTypes.bool,
    endDate: PropTypes.string,
    listData: PropTypes.array,
    loyaltyIconFunction: PropTypes.func,
    startDate: PropTypes.string,
    system: PropTypes.string,
    tableKey: PropTypes.string,
    tableLabel: PropTypes.string
};

VIPTrendsVipCustomersTable.defaultProps = {
    addField: true,
    allowLoyaltySelection: true,
    endDate: null,
    startDate: null,
    tableKey: 'customers',
    tableLabel: 'VIP Customers'
};

export default VIPTrendsVipCustomersTable;



// WEBPACK FOOTER //
// ./src/Fuse/components/VIPTrendsVipCustomersTable.js